/**
 * Auth state for the app: the signed-in Entra account, the redirect-response
 * handling, and a `getToken()` the API client calls on every request.
 *
 * One hook, used once at the App root and passed down. Not a context provider —
 * there is one consumer tree and a provider would be indirection for its own
 * sake.
 */
import { useCallback, useEffect, useRef, useState } from 'react'
import type { AccountInfo } from '@azure/msal-browser'
import {
  acquireToken,
  authEnabled,
  bootstrap,
  lastTokenErrorCode,
  login as msalLogin,
  logout as msalLogout,
} from './msal'

export interface AuthState {
  /** False while the Entra redirect response is still being processed. */
  ready: boolean
  account: AccountInfo | null
  error: string | null
  /** False when VITE_ENTRA_TENANT_ID/CLIENT_ID are unset (local dev). */
  enabled: boolean
  /**
   * The last token request failed for want of consent to the API scope, not for
   * want of a session. Re-authenticating cannot fix it; a directory admin must
   * grant the scope.
   */
  consentRequired: boolean
  login: () => void
  logout: () => void
  /** Current access token, or null when there is no usable session. */
  getToken: () => Promise<string | null>
}

export function useAuth(): AuthState {
  const [ready, setReady] = useState(!authEnabled)
  const [account, setAccount] = useState<AccountInfo | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [consentRequired, setConsentRequired] = useState(false)

  // StrictMode mounts effects twice in dev, and handleRedirectPromise consumes
  // a one-time response. msal.ts guards internally too, but the guard here also
  // stops a second setReady/setAccount pass. A ref, not state — state would not
  // have updated yet on the second synchronous mount.
  const started = useRef(false)

  useEffect(() => {
    if (!authEnabled || started.current) return
    started.current = true

    void (async () => {
      try {
        setAccount(await bootstrap())
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e))
      } finally {
        setReady(true)
      }
    })()
  }, [])

  const doLogin = useCallback(() => {
    setError(null)
    void msalLogin().catch((e: unknown) =>
      setError(e instanceof Error ? e.message : String(e)),
    )
  }, [])

  const doLogout = useCallback(() => {
    void msalLogout().then(() => {
      setAccount(null)
      setConsentRequired(false)
    })
  }, [])

  // Wrap acquireToken rather than reading lastTokenErrorCode() during render:
  // the module variable is set from the API client, outside React, so reading it
  // in a render body would only be correct when some other state change happened
  // to trigger that render. Recording it as state makes the re-render certain.
  const getToken = useCallback(async () => {
    const token = await acquireToken()
    setConsentRequired(
      token === null &&
        (lastTokenErrorCode() === 'consent_required' ||
          lastTokenErrorCode() === 'interaction_required'),
    )
    return token
  }, [])

  return {
    ready,
    account,
    error,
    enabled: authEnabled,
    consentRequired,
    login: doLogin,
    logout: doLogout,
    getToken,
  }
}
